import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  Switch,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';

import {
  buildPublicProfileUrl,
  changeOwnPassword,
  checkHandleAvailability,
  checkLoginAvailability,
  fetchHandleSuggestions,
  listBlockedUsers,
  unblockUser,
  type BlockedUserCard,
  deleteOwnAccount,
  HttpStatusError,
  patchNotificationSettings,
  patchNotificationShowText,
  patchUserShareSettings,
  ShareSettingsError,
  updateOwnProfile,
  type HandleAvailability,
} from '../../api/client';
import { isWrongCurrentPassword } from '../../auth/authFlowRules';
import {
  getNotifyPermissionStatus,
  requestNotifyPermission,
  type NotifyPermissionStatus,
} from '../../realtime/notifyIncomingMessage';
import { humanizeError } from '../../util/humanizeError';
import { useKeyboardOverlap } from '../../util/useKeyboardOverlap';
import type { UserGender } from '../../api/types';
import { PUBLIC_WEB_ORIGIN } from '../../config';
import { useAuth } from '../../auth/AuthContext';
import { LanguagePickerRow } from '../../components/LanguagePicker';
import { ThemePickerRow } from '../../components/ThemePickerRow';
import { useLocale } from '../../i18n/LocaleContext';
import type { MessageKey } from '../../i18n/messages';
import { chatListPalette } from '../../theme/chatListPalette';
import type { ChatListStyles } from '../../theme/chatListStylesFromPalette';
import {
  BIO_MAX_CHARS,
  DISPLAY_NAME_MAX_CHARS,
  LOGIN_MAX_CHARS,
  PASSWORD_MAX_CHARS,
  STATUS_TEXT_MAX_CHARS,
} from '../../util/inputLimits';
import { useVisualTheme } from '../../theme/VisualThemeContext';
import { hapticSelection, isHapticsEnabled, setHapticsEnabled } from '../../util/haptics';
import { writeHapticsEnabled } from '../../util/hapticsPrefStorage';
import { areSoundsEnabled, setSoundsEnabled } from '../../util/appSounds';
import { writeSoundsEnabled } from '../../util/soundPrefStorage';
import { invalidateAndroidChannels } from '../../notifications/androidChannels';
import { isKeepPhotoMetadata, writeKeepPhotoMetadata } from '../../util/photoMetadataPref';
import { chooseAction, confirmAction } from '../../util/confirmDialog';
import { normalizePublicHandleInput, publicHandleProblem } from '../../util/publicHandle';
import {
  BIRTH_AGE_MAX_YEARS,
  BIRTH_AGE_MIN_YEARS,
  birthDateBoundsIso,
  classifyBirthDateInput,
  formatBirthDateInput,
} from '../../util/birthdays';
import { DateTimeField } from '../../components/DateTimeField';
import { AvatarSettingsSection } from '../AvatarSettingsSection';
import { dirtySettingsSections, type SettingsSection } from './settingsDirty';
import { lastSettingsTab, rememberSettingsTab, type SettingsTab } from './settingsTabMemory';

type Props = {
  visible: boolean;
  /** Закрыть окно. Сброс полей делает сама модалка — снаружи о них знать нечего. */
  onClose: () => void;
};

/** Порядок вкладок = порядок в шапке; заголовок берётся из локали по этому же ключу. */
const TABS: { id: SettingsTab; label: MessageKey }[] = [
  { id: 'profile', label: 'settingsTabProfile' },
  { id: 'avatar', label: 'settingsTabAvatar' },
  { id: 'account', label: 'settingsTabAccount' },
  { id: 'privacy', label: 'settingsTabPrivacy' },
  { id: 'app', label: 'settingsTabPreferences' },
];

/**
 * Раскладка ряда вкладок — не геометрия кнопки: пять вкладок не помещаются в строку на телефоне
 * и переносятся, поэтому им нужны `flexGrow`/`flexBasis`, которых нет у чипов списка чатов.
 */
const TAB_CHIP_LAYOUT: ViewStyle = { flexGrow: 1, flexBasis: '30%', minWidth: 72 };

/**
 * Разрушающее действие (REL-1). Цвет задан здесь, а не в теме: во всём приложении красная
 * кнопка ровно одна, и заводить ради неё пару токенов в обеих темах — больше связей, чем
 * пользы. `#dc2626` читается и на светлом, и на тёмном фоне.
 *
 * Взводящая кнопка — контурная, подтверждающая — сплошная: разница должна быть видна боковым
 * зрением, а не только прочитанной надписью.
 */
const DANGER = '#dc2626';
const DANGER_BTN_OUTLINE: ViewStyle = { backgroundColor: 'transparent', borderWidth: 1, borderColor: DANGER };
const DANGER_BTN_SOLID: ViewStyle = { backgroundColor: DANGER, borderColor: DANGER };
const DANGER_TEXT = { color: DANGER };

/** Ссылки на документы (REL-4): подчёркнуты, чтобы читались как ссылки, а не как подпись. */
const LEGAL_LINK = { textDecorationLine: 'underline' as const, paddingVertical: 6 };

/** Зона нажатия текстовых кнопок-ссылок — не меньше 44 pt (аудит 2026-10-04, п. 16). */
const MIN_TAP: ViewStyle = { minHeight: 44, justifyContent: 'center' };

/** Строка списка заблокированных: имя слева, «Разблокировать» справа. */
const BLOCKED_ROW: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingVertical: 4,
};

/**
 * Пол: три чипа, а не выпадающий список. «Не указан» — такой же выбор, как остальные, и
 * стоит первым: это состояние по умолчанию, и вернуться в него человек должен так же просто,
 * как из него выйти.
 *
 * «Другой» убран по решению владельца 2026-08-29 в тот же день, когда поле завели.
 */
const GENDER_CHOICES: { value: UserGender | null; label: MessageKey }[] = [
  { value: null, label: 'genderUnset' },
  { value: 'MALE', label: 'genderMale' },
  { value: 'FEMALE', label: 'genderFemale' },
];

/**
 * Вкладка в шапке. Компонент модульный, а не объявленный внутри `SettingsModal`, — см.
 * `bindSettingsControls`.
 */
function SettingsTabChip({
  styles,
  label,
  active,
  onPress,
}: {
  styles: ChatListStyles;
  label: string;
  active: boolean;
  onPress: () => void;
}): React.ReactElement {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityLabel={label}
      accessibilityState={{ selected: active }}
      // В вебе `accessibilityState.selected` до DOM не доезжает: проверено в браузере — у
      // чипа не было `aria-selected` вовсе, и активную вкладку выдавал только цвет. С пятью
      // вкладками это уже не мелочь, поэтому атрибут ставим явно.
      aria-selected={active}
      // Геометрия — общая с чипами-фильтрами списка чатов (`settingsTabChip`); своим здесь
      // остаётся только раскладка: пять вкладок в две строки.
      style={[styles.settingsTabChip, TAB_CHIP_LAYOUT, active && styles.settingsTabChipActive]}>
      <Text
        style={[styles.settingsTabChipText, active && styles.settingsTabChipTextActive]}
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.85}>
        {label}
      </Text>
    </Pressable>
  );
}

/**
 * Единственная кнопка выхода из окна, одна и та же на всех вкладках.
 *
 * <p>«Готово» **сохраняет** несохранённое и закрывает (аудит удобства 2026-10-04, п. 38). Раньше
 * она спрашивала «Отменить изменения?» — то есть работала как «Отмена»: человек, нажавший
 * «Готово» после правки имени, получал предложение всё выбросить.
 */
function SettingsDoneRow({
  styles,
  label,
  busy,
  onPress,
}: {
  styles: ChatListStyles;
  label: string;
  busy: boolean;
  onPress: () => void;
}): React.ReactElement {
  return (
    <View style={styles.modalRow}>
      <Pressable
        style={[styles.modalBtn, styles.modalPrimary, busy ? styles.modalBtnDisabled : null]}
        onPress={onPress}
        disabled={busy}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled: busy, busy }}>
        {busy ? <ActivityIndicator size="small" color="#fff" /> : null}
        <Text style={[styles.modalBtnText, { color: '#fff' }]}>{label}</Text>
      </Pressable>
    </View>
  );
}

/**
 * Мелкие блоки формы, привязанные к таблице стилей темы.
 *
 * <p><b>Почему фабрика, а не функции внутри рендера.</b> Раньше `Group`, `SaveButton` и
 * `ToggleRow` объявлялись прямо в теле `SettingsModal`. Для React это каждый рендер НОВЫЙ тип
 * компонента, а значит — размонтировать старое поддерево и смонтировать новое. Внутри `Group`
 * живут поля ввода, поэтому на каждой набранной букве поле пересоздавалось: фокус слетал,
 * клавиатура закрывалась и открывалась заново, а окно «прыгало». Особенно заметно это было на
 * публичном нике во вкладке «Приватность» — набрать его целиком было практически нельзя.
 *
 * <p>Здесь тип создаётся один раз на тему (`useMemo` по `styles`), поэтому идентичность
 * компонента между рендерами не меняется и поддерево живёт.
 */
function bindSettingsControls(styles: ChatListStyles, toggleLabelColor: string) {
  /** Заголовок + рамка: раздел должен читаться как отдельная форма, а не как кусок списка. */
  function Group({ title, children }: { title: string; children: React.ReactNode }) {
    return (
      <View style={styles.settingsGroupCard}>
        <Text style={styles.settingsGroupTitle} accessibilityRole="header">
          {title}
        </Text>
        {children}
      </View>
    );
  }

  /**
   * Кнопка сохранения раздела. Индикатор обязателен: на медленной сети нажатие без него выглядит
   * как «ничего не произошло», и человек жмёт второй раз — то есть шлёт второй запрос.
   */
  function SaveButton({
    label,
    busy,
    disabled,
    onPress,
  }: {
    label: string;
    busy: boolean;
    disabled?: boolean;
    onPress: () => void;
  }) {
    const off = busy || Boolean(disabled);
    return (
      <Pressable
        style={[
          styles.modalBtn,
          styles.modalPrimary,
          // Ряд с индикатором и просвет 8 приходят из controlPill внутри modalBtn — здесь
          // остаётся только выравнивание самой кнопки в колонке.
          { alignSelf: 'flex-start' },
          off ? styles.modalBtnDisabled : null,
        ]}
        onPress={onPress}
        disabled={off}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled: off, busy }}>
        {busy ? <ActivityIndicator size="small" color="#fff" /> : null}
        <Text style={[styles.modalBtnText, { color: '#fff' }]}>{label}</Text>
      </Pressable>
    );
  }

  function ToggleRow({
    label,
    value,
    onValueChange,
    disabled,
  }: {
    label: string;
    value: boolean;
    onValueChange: (next: boolean) => void;
    disabled?: boolean;
  }) {
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ flex: 1, paddingRight: 12, color: toggleLabelColor }}>{label}</Text>
        <Switch value={value} onValueChange={onValueChange} disabled={disabled} accessibilityLabel={label} />
      </View>
    );
  }

  return { Group, SaveButton, ToggleRow };
}

/** Профиль, как он лежит на сервере: `PATCH /api/users/me` перезаписывает его целиком. */
type SavedProfile = {
  login: string;
  displayName: string;
  bio: string | null;
  statusText: string | null;
  gender: UserGender | null;
  birthDate: string | null;
};

/**
 * Идёт ли набранный ник на сервер за проверкой занятости. Пустое поле — это «снять ник», а не
 * ошибка; свой же ник проверять не у кого; формат, зарезервированные и брань видно и без
 * сети — на них запрос не тратим.
 */
function handleNeedsServerCheck(normalized: string, savedHandle: string): boolean {
  if (normalized.length === 0 || normalized === savedHandle) return false;
  const localProblem = publicHandleProblem(normalized);
  return localProblem === null || localProblem === 'empty';
}

/**
 * Окно настроек (стори 18.1, перестроено в 18.6).
 *
 * <p>Раньше эти ~350 строк разметки и два десятка состояний жили внутри `ChatListScreen.tsx`
 * (3 500 строк) — это половина ARCH-1/UX-3. Здесь они целиком: список чатов теперь знает про
 * настройки ровно две вещи — открыты они или нет.
 *
 * <p>Стори 18.6. Вкладка «Аккаунт» была свалкой: четыре формы подряд (профиль, публичный ник со
 * ссылкой, пароль, системный блок Android), четыре «Сохранить» и две серые кнопки без объяснения
 * — по прямой жалобе владельца «полная каша, разделы не разведены, на кнопках нет отклика».
 * Теперь у каждой вкладки одна тема и не больше двух форм:
 *
 * <ul>
 *   <li>«Профиль» — имя, статус, о себе (`PATCH /api/users/me`);
 *   <li>«Фото» — аватар;
 *   <li>«Аккаунт» — только вход: логин (`PATCH /api/users/me`) и пароль
 *       (`PATCH /api/users/me/password`);
 *   <li>«Приватность» — публичный ник и ссылка, видимость, метаданные снимков
 *       (`PATCH /api/users/me/share-settings`);
 *   <li>«Приложение» — тема, язык, тактильная отдача и системные настройки устройства.
 * </ul>
 *
 * <p>Две формы правят один и тот же `PATCH /api/users/me`, который перезаписывает профиль
 * целиком. Поэтому каждая шлёт СОХРАНЁННЫЕ значения чужих полей (`savedProfile`), а не то, что
 * лежит в соседней вкладке: иначе сохранение логина утаскивало бы за собой недописанное имя.
 *
 * <p>Ответ на «на кнопках нет отклика»: подтверждения больше не идут через `Alert.alert` —
 * в вебе это пустая функция (`react-native-web`), и нажатие «Скопировать» не показывало ничего.
 * Вместо него строка-уведомление внутри окна, одинаковая на всех платформах. Тумблеры
 * видимости сохраняются сами: раньше они молча ждали кнопку «Сохранить» ниже по списку.
 */
export function SettingsModal({ visible, onClose }: Props): React.ReactElement {
  const window = useWindowDimensions();
  /**
   * Клавиатура в окне настроек. Окно — Modal по центру, и обходить клавиатуру оно не умело:
   * на вкладке «Аккаунт» поле текущего пароля уходило под неё, а «Новый пароль» и «Сохранить»
   * не было видно вовсе (прогон 2026-10-05, сценарий 23). Теперь окно встаёт над клавиатурой,
   * свиток ужимается до места над ней, а поле с курсором само въезжает в видимую часть.
   */
  const keyboard = useKeyboardOverlap(visible);
  const settingsScrollRef = useRef<ScrollView>(null);
  const settingsContentRef = useRef<View>(null);
  useEffect(() => {
    if (keyboard <= 0) return;
    const timer = setTimeout(() => {
      const input = TextInput.State.currentlyFocusedInput();
      const content = settingsContentRef.current;
      if (!input || !content) return;
      input.measureLayout(
        content,
        (_x, y) => settingsScrollRef.current?.scrollTo({ y: Math.max(0, y - 48), animated: true }),
        () => undefined,
      );
    }, 60);
    return () => clearTimeout(timer);
  }, [keyboard]);
  const {
    user,
    applyAuth,
    logout,
    mergeUserProfile,
    mergeUserShareSettings,
    mergeUserDoNotDisturb,
    mergeUserShowMessageText,
  } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { visualTheme, resolvedVisualTheme, setVisualTheme, chatListStyles: styles } = useVisualTheme();
  const listPal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);

  const [tab, setTab] = useState<SettingsTab>(lastSettingsTab);

  /** Строка-подтверждение поверх содержимого: замена `Alert.alert`, мёртвого в вебе. */
  const [notice, setNotice] = useState<string | null>(null);

  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profileStatusText, setProfileStatusText] = useState('');
  /** Пол и дата рождения (заказ владельца 2026-08-29). Дата — строкой, как её печатают. */
  const [profileGender, setProfileGender] = useState<UserGender | null>(null);
  const [profileBirthDate, setProfileBirthDate] = useState('');
  /**
   * Границы календаря даты рождения — по одному разу на открытие окна.
   *
   * <p>Пересчёт на каждом открытии (см. разбор открытия ниже), а не однажды: окно смонтировано
   * всё время работы приложения и лишь прячется, поэтому посчитанное однажды «сегодня» пережило
   * бы полночь и сдвинуло бы вилку на сутки. Пересчитывать на каждый рендер тоже нельзя: новый
   * объект дёргал бы поле впустую.
   */
  const [birthDateCalendarBounds, setBirthDateCalendarBounds] = useState(birthDateBoundsIso);
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  const [profileLogin, setProfileLogin] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginSaved, setLoginSaved] = useState(false);
  /**
   * Ответ сервера о занятости логина — вместе с тем, про какой логин он. Правка поля его
   * сбрасывает, поэтому «идёт проверка» — это просто «ответа про набранное ещё нет».
   */
  const [loginServerCheck, setLoginServerCheck] = useState<{
    login: string;
    available: boolean | null;
  } | null>(null);

  const [shareHandle, setShareHandle] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareSaved, setShareSaved] = useState(false);
  /** Ответ проверки занятости; `null` — ещё не спрашивали или спрашивать нечего. */
  const [handleCheck, setHandleCheck] = useState<HandleAvailability | null>(null);
  /**
   * Для какого набранного ника сервер уже ответил (удачно или нет). Правка поля сбрасывает;
   * пока не совпадает с набранным — идёт проверка.
   */
  const [handleCheckedFor, setHandleCheckedFor] = useState<string | null>(null);
  /**
   * Свободные варианты, когда набранный ник занят.
   *
   * <p>Раньше человек в этом месте упирался в «Ник занят» и придумывал следующий сам — по
   * запросу на попытку. Считает варианты сервер: у него и так есть ответ на вопрос «что
   * свободно», а тут они превращаются в один тап.
   *
   * <p>Гарантии за ними нет и быть не может: ник может уйти между подсказкой и сохранением.
   * Поэтому нажатие подставляет вариант в поле, а не сохраняет его молча, — и обычная проверка
   * занятости идёт своим чередом.
   */
  const [handleSuggestions, setHandleSuggestions] = useState<string[]>([]);

  /** Логин: короткий и свой не проверяются — ответ на них известен без сети. */
  const loginNormalized = profileLogin.trim().toLowerCase();
  const loginLocalAvailable: boolean | null | undefined =
    loginNormalized.length < 2
      ? null
      : user?.login?.trim().toLowerCase() === loginNormalized
        ? true
        : undefined;
  const profileLoginCheckBusy =
    loginLocalAvailable === undefined && loginServerCheck?.login !== loginNormalized;
  const profileLoginAvailable =
    loginLocalAvailable !== undefined
      ? loginLocalAvailable
      : loginServerCheck?.login === loginNormalized
        ? loginServerCheck.available
        : null;

  /**
   * Состояние кнопок шаринга (стори 18.3): опора — сохранённый профиль, а не текст поля и не
   * положение тумблера. И то и другое до нажатия «Сохранить» существует только на экране, а
   * ссылка уходит наружу и живёт в чужой переписке.
   */
  const savedShareHandle = (user?.publicHandle ?? '').trim().toLowerCase();
  /** Каноническая форма набранного — та, что уйдёт в базу (стори 18.2). */
  const handlePreview = normalizePublicHandleInput(shareHandle);
  const handleCheckBusy =
    handleNeedsServerCheck(handlePreview, savedShareHandle) && handleCheckedFor !== handlePreview;
  /**
   * Занятый ник, под который показываются варианты. Только пока окно открыто и набранное вообще
   * идёт на сервер: пустое поле, свой ник и местный отказ подсказок не дают.
   */
  const takenHandleForSuggestions =
    visible &&
    handleNeedsServerCheck(handlePreview, savedShareHandle) &&
    handleCheck &&
    !handleCheck.available &&
    handleCheck.problem === 'taken'
      ? handleCheck.handle
      : null;
  // Ушли с занятого ника — подсказки гаснут сразу; смена одного занятого на другой держит
  // прежние, пока не придут новые.
  const [suggestionsTakenHandle, setSuggestionsTakenHandle] = useState<string | null>(null);
  if (suggestionsTakenHandle !== takenHandleForSuggestions) {
    setSuggestionsTakenHandle(takenHandleForSuggestions);
    if (takenHandleForSuggestions === null) setHandleSuggestions([]);
  }

  const [shareProfilePublic, setShareProfilePublic] = useState(true);
  const [shareAllowDiscovery, setShareAllowDiscovery] = useState(true);
  const [visibilityBusy, setVisibilityBusy] = useState(false);
  const [visibilityError, setVisibilityError] = useState<string | null>(null);
  const [visibilitySaved, setVisibilitySaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  /**
   * «Неверный текущий пароль» — под полем текущего пароля, а не под кнопкой (п. 9): ошибка
   * относится к одному полю из двух, и показывать её надо там, где исправлять.
   */
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [passwordSaved, setPasswordSaved] = useState(false);

  /** «Готово» сохраняет разделы по очереди — пока идёт, кнопка занята. */
  const [doneBusy, setDoneBusy] = useState(false);

  /** «Не беспокоить» в разделе «Уведомления»: положение на время запроса, как в быстрых настройках. */
  const [dndDraft, setDndDraft] = useState<boolean | null>(null);
  const [dndError, setDndError] = useState<string | null>(null);
  /** «Показывать текст в уведомлениях» (PRIV-5) — так же: положение на время запроса. */
  const [showTextDraft, setShowTextDraft] = useState<boolean | null>(null);
  const [showTextError, setShowTextError] = useState<string | null>(null);
  /** Разрешение на уведомления — для кнопки «Включить» в вебе и строки состояния. */
  const [notifyStatus, setNotifyStatus] = useState<NotifyPermissionStatus | null>(null);

  /**
   * Удаление аккаунта (REL-1).
   *
   * `deleteArmed` — второй экран того же раздела, а не диалог: `Alert.alert` в вебе пустая
   * функция (UX-10), и подтверждение необратимой операции молча не показалось бы вовсе.
   * Поля живут здесь же и очищаются при закрытии окна вместе с остальными.
   */
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteWord, setDeleteWord] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  /**
   * Заблокированные (REL-2). Список подтягивается при первом открытии вкладки «Приватность», а
   * не при открытии окна: у большинства он пуст, и платить за него запросом на каждый заход в
   * настройки незачем.
   */
  const [blocked, setBlocked] = useState<BlockedUserCard[]>([]);
  const [blockedBusy, setBlockedBusy] = useState(false);
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const blockedLoadedRef = useRef(false);

  useEffect(() => {
    if (!visible || tab !== 'privacy' || blockedLoadedRef.current) return;
    blockedLoadedRef.current = true;
    setBlockedBusy(true);
    listBlockedUsers()
      .then(setBlocked)
      .catch((e: unknown) => setBlockedError(humanizeError(e, t('blockedListLoadFailed'))))
      .finally(() => setBlockedBusy(false));
  }, [tab, visible, t]);

  async function submitUnblock(userId: string) {
    setBlockedError(null);
    try {
      await unblockUser(userId);
      setBlocked((prev) => prev.filter((b) => b.userId !== userId));
    } catch (e) {
      setBlockedError(humanizeError(e, t('unblockFailed')));
    }
  }

  /**
   * Состояние разрешения на уведомления — при открытии вкладки «Приложение»: человек мог
   * поменять его в системе, пока окно было закрыто.
   */
  useEffect(() => {
    if (!visible || tab !== 'app') return;
    let cancelled = false;
    void getNotifyPermissionStatus().then((s) => {
      if (!cancelled) setNotifyStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, [tab, visible]);

  async function enableNotifications() {
    setNotifyStatus(await requestNotifyPermission());
  }

  /** «Не беспокоить» — той же ручкой, что и в быстрых настройках; сохраняется сам. */
  async function toggleDnd(next: boolean) {
    setDndDraft(next);
    setDndError(null);
    try {
      const saved = await patchNotificationSettings(next);
      await mergeUserDoNotDisturb(saved);
    } catch (e) {
      setDndError(humanizeError(e, t('dndFailed')));
    } finally {
      setDndDraft(null);
    }
  }

  /**
   * «Показывать текст в уведомлениях» (PRIV-5). Сохраняется сам и на сервере: пуш собирает
   * сервер, и спрятать текст на устройстве значило бы всё равно отдать его FCM/APNs.
   */
  async function toggleShowText(next: boolean) {
    setShowTextDraft(next);
    setShowTextError(null);
    try {
      const saved = await patchNotificationShowText(next);
      await mergeUserShowMessageText(saved);
    } catch (e) {
      setShowTextError(humanizeError(e, t('dndFailed')));
    } finally {
      setShowTextDraft(null);
    }
  }

  /** Выход — с подтверждением (п. 12): кнопка в шапке списка чатов выходила с одного касания. */
  async function confirmLogout() {
    const ok = await confirmAction({
      title: t('logoutConfirmTitle'),
      body: t('logoutConfirmBody'),
      confirmText: t('logout'),
      cancelText: t('settingsDeleteAccountCancel'),
      destructive: true,
    });
    if (!ok) return;
    discardAndClose();
    await logout();
  }

  /** Локальные настройки: применяются сразу, сервер о них не знает. */
  const [hapticsEnabled, setHapticsEnabledState] = useState(isHapticsEnabled);
  const [soundsEnabled, setSoundsEnabledState] = useState(areSoundsEnabled);
  const [keepPhotoMetadata, setKeepPhotoMetadata] = useState(isKeepPhotoMetadata);

  const savedFlashTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      for (const timer of savedFlashTimersRef.current) clearTimeout(timer);
      savedFlashTimersRef.current = [];
    },
    [],
  );

  /** Показывает «сохранено» и сам гасит его — подтверждение не должно висеть до закрытия окна. */
  function flashSaved(setter: (value: boolean) => void) {
    setter(true);
    const timer = setTimeout(() => setter(false), 2500);
    savedFlashTimersRef.current.push(timer);
  }

  /** Разовое сообщение о действии без формы («Скопировано») — там, где раньше был `Alert`. */
  function showNotice(text: string) {
    setNotice(text);
    const timer = setTimeout(() => setNotice((cur) => (cur === text ? null : cur)), 2500);
    savedFlashTimersRef.current.push(timer);
  }

  /**
   * Сохранённый профиль: `PATCH /api/users/me` перезаписывает его целиком, а форм теперь две
   * («Профиль» и логин в «Аккаунте»). Каждая правит своё поле и шлёт эти значения для чужих.
   */
  function savedProfile(): SavedProfile {
    return {
      login: (user?.login ?? '').trim(),
      displayName: (user?.displayName ?? '').trim(),
      bio: user?.bio ?? null,
      statusText: user?.statusText ?? null,
      gender: user?.gender ?? null,
      birthDate: user?.birthDate ?? null,
    };
  }

  function resetForms() {
    setProfileLogin(user?.login ?? '');
    setProfileDisplayName(user?.displayName ?? '');
    setProfileBio(user?.bio ?? '');
    setProfileStatusText(user?.statusText ?? '');
    setProfileGender(user?.gender ?? null);
    setProfileBirthDate(formatBirthDateInput(user?.birthDate));
    setShareHandle(user?.publicHandle ?? '');
    setShareProfilePublic(user?.profilePublic !== false);
    setShareAllowDiscovery(user?.allowDiscoveryByHandle !== false);
    setLoginServerCheck(null);
    setHandleCheck(null);
    setHandleCheckedFor(null);
    setProfileError(null);
    setLoginError(null);
    setShareError(null);
    setVisibilityError(null);
    setPasswordError(null);
    setCurrentPasswordError(null);
    setDndError(null);
    setShowTextError(null);
    setNotice(null);
  }

  /** Что не сохранено (стори 18.4, п. 38). Правило — в `settingsDirty`, под тестами. */
  function dirtySections(): SettingsSection[] {
    return dirtySettingsSections(
      {
        login: profileLogin,
        displayName: profileDisplayName,
        statusText: profileStatusText,
        bio: profileBio,
        publicHandle: shareHandle,
        currentPassword,
        newPassword,
        gender: profileGender,
        birthDate: profileBirthDate,
      },
      {
        login: user?.login,
        displayName: user?.displayName,
        statusText: user?.statusText,
        bio: user?.bio,
        publicHandle: user?.publicHandle,
        gender: user?.gender,
        birthDate: user?.birthDate,
      },
    );
  }

  function discardAndClose() {
    resetForms();
    setCurrentPassword('');
    setNewPassword('');
    // REL-1. Взведённое удаление не должно пережить закрытие окна: следующее открытие настроек
    // встретило бы человека формой «удалить навсегда» с уже введённым паролем.
    disarmAccountDeletion();
    onClose();
  }

  function disarmAccountDeletion() {
    setDeleteArmed(false);
    setDeletePassword('');
    setDeleteWord('');
    setDeleteError(null);
    setDeletePasswordError(null);
  }

  /** Вкладка, на которой живёт раздел: туда ведём, если его сохранение не удалось. */
  const SECTION_TAB: Record<SettingsSection, SettingsTab> = {
    profile: 'profile',
    login: 'account',
    handle: 'privacy',
    password: 'account',
  };

  /**
   * «Готово» и «Сохранить» в вопросе при закрытии (п. 38): сохранить всё несохранённое по
   * разделам и закрыть. На первой неудаче окно остаётся открытым на вкладке этого раздела —
   * ошибка там уже стоит под полем, — а сохранённое до неё не откатывается: каждый раздел
   * пишется своей ручкой и сам по себе уже на сервере.
   */
  async function saveDirtyAndClose() {
    const sections = dirtySections();
    if (sections.length === 0) {
      discardAndClose();
      return;
    }
    setDoneBusy(true);
    try {
      // Профиль, сохранённый первым, — опора для логина: обе формы пишут профиль целиком, и
      // логин не должен уйти со старым именем из `user`, который ещё не перечитался.
      let base: SavedProfile | undefined;
      for (const section of sections) {
        let ok: boolean;
        if (section === 'profile') {
          const saved = await submitProfileUpdate();
          ok = saved !== null;
          if (saved) base = saved;
        } else if (section === 'login') {
          ok = await submitLoginUpdate(base);
        } else if (section === 'handle') {
          ok = await submitHandle();
        } else {
          ok = await submitPasswordChange();
        }
        if (!ok) {
          if (tab !== SECTION_TAB[section]) selectTab(SECTION_TAB[section]);
          showNotice(t('settingsSaveFailedStay'));
          return;
        }
      }
      discardAndClose();
    } finally {
      setDoneBusy(false);
    }
  }

  /**
   * Закрытие окна крестиком, «назад» или Escape (стори 18.4, п. 38). Раньше любой выход молча
   * выбрасывал напечатанное, потом стал спрашивать «выбросить?». Теперь вопрос — с тремя
   * ответами, как в почтовых клиентах: сохранить, не сохранять, вернуться. Спрашиваем только
   * когда есть что терять, иначе подтверждение превращается в шум на каждом закрытии.
   */
  async function close() {
    if (dirtySections().length === 0) {
      discardAndClose();
      return;
    }
    const choice = await chooseAction({
      title: t('settingsUnsavedTitle'),
      body: t('settingsUnsavedBody'),
      buttons: [
        { text: t('settingsUnsavedSave') },
        { text: t('settingsUnsavedDiscard'), style: 'destructive' },
        { text: t('settingsUnsavedStay'), style: 'cancel' },
      ],
    });
    if (choice === 0) await saveDirtyAndClose();
    else if (choice === 1) discardAndClose();
  }

  function selectTab(next: SettingsTab) {
    setTab(next);
    rememberSettingsTab(next);
    // Уведомление относится к вкладке, на которой было вызвано: тащить «Скопировано» в другой
    // раздел — тот же мусор, от которого мы уводим.
    setNotice(null);
    hapticSelection();
  }

  // Открытие: поля заполняются текущим профилем, локальные настройки берутся из памяти —
  // они уже прогреты на старте приложения, поэтому переключатели не мигают. Делается прямо в
  // рендере по смене `visible` («прошлое значение в состоянии»), а не эффектом: эффект показал
  // бы первый кадр открытого окна со старыми полями. Профиль из контекста читается только в
  // момент открытия — пересобирать форму на каждое его поле незачем.
  const [shownVisible, setShownVisible] = useState(false);
  if (shownVisible !== visible) {
    setShownVisible(visible);
    if (visible) {
      resetForms();
      setHapticsEnabledState(isHapticsEnabled());
      setSoundsEnabledState(areSoundsEnabled());
      setKeepPhotoMetadata(isKeepPhotoMetadata());
      setBirthDateCalendarBounds(birthDateBoundsIso());
    }
  }

  /** Правка логина: прежний ответ сервера про него больше не годится. */
  function changeProfileLogin(value: string) {
    setProfileLogin(value);
    setLoginServerCheck(null);
  }

  /** Правка ника — тем же порядком, что у логина. */
  function changeShareHandle(value: string) {
    setShareHandle(value);
    setHandleCheckedFor(null);
    // Ушли на пустое, свой или заведомо негодный ник — прошлый ответ сервера к нему не относится.
    if (!handleNeedsServerCheck(normalizePublicHandleInput(value), savedShareHandle)) {
      setHandleCheck(null);
    }
  }

  useEffect(() => {
    if (!visible) return;
    const normalized = profileLogin.trim().toLowerCase();
    // Короткий и свой логин отвечены без сети — см. loginLocalAvailable.
    if (normalized.length < 2 || user?.login?.trim().toLowerCase() === normalized) return;
    // Ответ на уже переписанный логин не нужен: иначе опоздавший ответ про «abc» затёр бы
    // свежий про «abcd», и проверка повисла бы.
    let superseded = false;
    const timer = setTimeout(() => {
      void checkLoginAvailability(normalized)
        .then((available) => {
          if (!superseded) setLoginServerCheck({ login: normalized, available });
        })
        .catch(() => {
          if (!superseded) setLoginServerCheck({ login: normalized, available: null });
        });
    }, 280);
    return () => {
      superseded = true;
      clearTimeout(timer);
    };
  }, [profileLogin, visible, user?.login]);

  /**
   * Занятость ника (стори 18.2) — тем же механизмом, что у логина: пауза 280 мс, чтобы не
   * бить в сервер на каждый символ. У логина такая проверка была с самого начала, у ника —
   * нет, хотя именно ник виден снаружи и занимается навсегда.
   */
  useEffect(() => {
    if (!visible) {
      return;
    }
    const normalized = normalizePublicHandleInput(shareHandle);
    // Пустое, свой и заведомо негодный ник на сервер не ходят — см. handleNeedsServerCheck.
    if (!handleNeedsServerCheck(normalized, savedShareHandle)) return;
    let superseded = false;
    const timer = setTimeout(() => {
      void checkHandleAvailability(normalized)
        .then((r) => {
          if (!superseded) setHandleCheck(r);
        })
        .catch(() => {
          if (!superseded) setHandleCheck(null);
        })
        .finally(() => {
          if (!superseded) setHandleCheckedFor(normalized);
        });
    }, 280);
    return () => {
      superseded = true;
      clearTimeout(timer);
    };
  }, [shareHandle, visible, savedShareHandle]);

  /**
   * Варианты запрашиваются только на занятый ник, и только когда проверка уже ответила: на
   * ошибку формата подсказывать нечего (человек допечатывает), а спрашивать их заранее — это
   * лишний запрос на каждый символ.
   */
  useEffect(() => {
    // Гашение подсказок, когда занятого ника больше нет, — в рендере, рядом с takenHandleForSuggestions.
    if (takenHandleForSuggestions === null) return;
    let cancelled = false;
    void fetchHandleSuggestions(takenHandleForSuggestions)
      .then((rows) => {
        if (!cancelled) setHandleSuggestions(rows);
      })
      .catch(() => {
        // Молча: подсказка — это удобство поверх понятного отказа, и её отсутствие ничего не
        // ломает. Ошибку показывать здесь значило бы сообщить о поломке того, чего человек не
        // просил.
        if (!cancelled) setHandleSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [takenHandleForSuggestions]);

  /** Вкладка «Профиль»: имя, статус, о себе. Логин сюда не входит — он во вкладке «Аккаунт». */
  /** Возвращает сохранённый профиль или `null`, если сохранить не вышло (ошибка — под формой). */
  async function submitProfileUpdate(): Promise<SavedProfile | null> {
    const displayName = profileDisplayName.trim();
    const saved = savedProfile();
    if (!displayName) {
      // Раньше здесь было общее «Ошибка» — без слова о том, что не так (п. 8).
      setProfileError(t('settingsNameRequired'));
      return null;
    }
    if (saved.login.length < 2) {
      setProfileError(t('errorSomethingWrong'));
      return null;
    }
    // Дата рождения проверяется до похода в сеть: сервер ответил бы «Invalid birth date»
    // по-английски, а человек печатал её на своём языке и в своём формате.
    //
    // Причина отказа разная, и сообщение разное: «введите ДД.ММ.ГГГГ» в ответ на аккуратно
    // набранное «01.02.2024» ничего не объясняет — формат человек угадал, не угадал он вилку
    // возраста, о которой из такого текста не догадаться.
    const parsedBirthDate = classifyBirthDateInput(profileBirthDate);
    if (!parsedBirthDate.ok) {
      setProfileError(
        t(parsedBirthDate.reason === 'age' ? 'settingsBirthDateAgeRange' : 'settingsBirthDateInvalid')
          .replace('{min}', String(BIRTH_AGE_MIN_YEARS))
          .replace('{max}', String(BIRTH_AGE_MAX_YEARS)),
      );
      return null;
    }
    const birthDate = parsedBirthDate.iso;
    setProfileBusy(true);
    setProfileError(null);
    try {
      const bio = profileBio.trim() || null;
      const statusText = profileStatusText.trim() || null;
      const birth = birthDate === '' ? null : birthDate;
      await updateOwnProfile(saved.login, displayName, bio, statusText, profileGender, birth);
      const next: SavedProfile = {
        login: saved.login,
        displayName,
        bio,
        statusText,
        gender: profileGender,
        birthDate: birth,
      };
      await mergeUserProfile(next);
      setProfileError(null);
      flashSaved(setProfileSaved);
      return next;
    } catch (e) {
      setProfileError(humanizeError(e, t('profileUpdateFailed')));
      return null;
    } finally {
      setProfileBusy(false);
    }
  }

  /**
   * Вкладка «Аккаунт»: только логин. Остальной профиль уходит сохранённым, а не из полей.
   *
   * @param base профиль, только что сохранённый той же кнопкой «Готово»: `user` из контекста в
   *     этом рендере ещё старый, и логин иначе ушёл бы с прежним именем, затерев новое
   */
  async function submitLoginUpdate(base?: SavedProfile): Promise<boolean> {
    const login = profileLogin.trim();
    if (login.length < 2) {
      setLoginError(t('settingsLoginHint'));
      return false;
    }
    if (profileLoginCheckBusy || profileLoginAvailable === false) {
      setLoginError(t('loginUnavailable'));
      return false;
    }
    const saved = base ?? savedProfile();
    // Имя на сервере обязательно и непусто у всех, но подстраховка дешевле отказа 400.
    const displayName = saved.displayName || profileDisplayName.trim() || login;
    setLoginBusy(true);
    setLoginError(null);
    try {
      // Вкладка «Аккаунт» правит только логин: остальной профиль — включая пол и дату
      // рождения — уходит СОХРАНЁННЫМ, иначе смена логина стирала бы их вместе с собой.
      await updateOwnProfile(
        login,
        displayName,
        saved.bio,
        saved.statusText,
        saved.gender,
        saved.birthDate,
      );
      await mergeUserProfile({
        login,
        displayName,
        bio: saved.bio,
        statusText: saved.statusText,
        gender: saved.gender,
        birthDate: saved.birthDate,
      });
      flashSaved(setLoginSaved);
      return true;
    } catch (e) {
      setLoginError(humanizeError(e, t('profileUpdateFailed')));
      return false;
    } finally {
      setLoginBusy(false);
    }
  }

  /**
   * Сохранение ника (стори 18.2, сужено в 18.6).
   *
   * <p>Запрос частичный — только `publicHandle`. Тумблеры видимости пишутся сами и сюда больше
   * не примешиваются: иначе «Сохранить ник» тихо утаскивал бы за собой их положение с соседней
   * карточки.
   *
   * <p>В стор кладётся то, что вернул сервер, а не то, что напечатал человек: сервер приводит
   * ник к канонической форме, и раньше настройки показывали «My-Handle», пока в базе лежало
   * `my_handle` — расхождение жило до ближайшего обновления профиля.
   *
   * <p>Ошибка переводится по коду ответа: текст сервера английский во всех локалях.
   */
  async function saveHandle() {
    setShareBusy(true);
    setShareError(null);
    try {
      const trimmed = shareHandle.trim();
      const saved = await patchUserShareSettings({ publicHandle: trimmed === '' ? '' : trimmed });
      await mergeUserShareSettings({ publicHandle: saved.publicHandle });
      // Поле подтягивается к сохранённому: иначе оно осталось бы с «My-Handle», а кнопки
      // шаринга считали бы это несохранённой правкой.
      setShareHandle(saved.publicHandle ?? '');
      setHandleCheck(null);
      setHandleCheckedFor(null);
      flashSaved(setShareSaved);
      return true;
    } catch (e) {
      if (e instanceof ShareSettingsError) {
        setShareError(
          e.problem === 'taken'
            ? t('settingsHandleTaken')
            : e.problem === 'invalid'
              ? t('settingsHandleSaveFailedInvalid')
              : humanizeError(e, t('profileUpdateFailed')),
        );
      } else {
        setShareError(humanizeError(e, t('profileUpdateFailed')));
      }
      return false;
    } finally {
      setShareBusy(false);
    }
  }

  /**
   * Смена и снятие ника — операции с последствиями за пределами экрана: старые ссылки перестают
   * работать, а освободившийся ник немедленно доступен другим (карантина нет, см. вопрос 1 в
   * эпике). Поэтому спрашиваем, а не делаем молча.
   */
  async function submitHandle(): Promise<boolean> {
    const next = normalizePublicHandleInput(shareHandle);
    const had = savedShareHandle.length > 0;

    // «Готово» зовёт сюда и с заведомо негодным ником — строка под полем уже объясняет
    // почему, а поход на сервер за тем же отказом ничего не добавит.
    if (shareSaveBlocked) {
      if (handleStatusText && handleStatusIsError) setShareError(handleStatusText);
      return false;
    }

    if (had && next.length === 0) {
      const ok = await confirmAction({
        title: t('settingsHandleConfirmClearTitle'),
        body: t('settingsHandleConfirmClearBody'),
        confirmText: t('settingsSaveShareSettings'),
        cancelText: t('settingsHandleKeep'),
        destructive: true,
      });
      if (!ok) return false;
    } else if (had && next !== savedShareHandle) {
      const ok = await confirmAction({
        title: t('settingsHandleConfirmChangeTitle'),
        body: t('settingsHandleConfirmChangeBody'),
        confirmText: t('settingsSaveShareSettings'),
        cancelText: t('settingsHandleKeep'),
      });
      if (!ok) return false;
    }
    return saveHandle();
  }

  /**
   * Тумблеры видимости сохраняются сами (стори 18.6).
   *
   * <p>Раньше они меняли только состояние экрана и ждали кнопку «Сохранить ник и приватность»
   * ниже по списку — нажатие выглядело как «ничего не произошло», а закрытие окна тихо
   * откатывало выбор. Здесь переключатель сразу уходит на сервер, а при отказе возвращается
   * в прежнее положение: показывать включённым то, что сервер не принял, нельзя.
   */
  async function toggleVisibility(patch: { profilePublic?: boolean; allowDiscoveryByHandle?: boolean }) {
    const prevPublic = shareProfilePublic;
    const prevDiscovery = shareAllowDiscovery;
    if (patch.profilePublic !== undefined) setShareProfilePublic(patch.profilePublic);
    if (patch.allowDiscoveryByHandle !== undefined) setShareAllowDiscovery(patch.allowDiscoveryByHandle);
    setVisibilityBusy(true);
    setVisibilityError(null);
    try {
      const saved = await patchUserShareSettings(patch);
      await mergeUserShareSettings({
        profilePublic: saved.profilePublic,
        allowDiscoveryByHandle: saved.allowDiscoveryByHandle,
      });
      setShareProfilePublic(saved.profilePublic);
      setShareAllowDiscovery(saved.allowDiscoveryByHandle);
      flashSaved(setVisibilitySaved);
    } catch (e) {
      setShareProfilePublic(prevPublic);
      setShareAllowDiscovery(prevDiscovery);
      setVisibilityError(humanizeError(e, t('profileUpdateFailed')));
    } finally {
      setVisibilityBusy(false);
    }
  }

  async function submitPasswordChange(): Promise<boolean> {
    setCurrentPasswordError(null);
    if (!currentPassword.trim()) {
      setCurrentPasswordError(t('settingsCurrentPasswordRequired'));
      return false;
    }
    if (newPassword.trim().length < 8) {
      setPasswordError(t('settingsNewPasswordTooShort'));
      return false;
    }
    setPasswordBusy(true);
    setPasswordError(null);
    try {
      const session = await changeOwnPassword(currentPassword, newPassword);
      // Стори 18.5: сервер отозвал все сессии — своё устройство остаётся в строю только с этой
      // новой парой. Без неё человек был бы разлогинен ровно за то, что сменил пароль.
      await applyAuth(session);
      // Стори 18.1: окно остаётся открытым и говорит, что произошло. Раньше успешная смена
      // пароля просто гасила модалку — вместе с несохранёнными правками профиля и ника, и без
      // единого слова о том, сменился пароль или нет. Полноценная защита от потери правок —
      // стори 18.4, здесь ровно подтверждение и очистка своих полей.
      setCurrentPassword('');
      setNewPassword('');
      flashSaved(setPasswordSaved);
      return true;
    } catch (e) {
      // Опечатка в текущем пароле — под его полем и своими словами (п. 9). Сессия при этом
      // цела: сервер отвечает 403, а не 401, и клиент больше не выкидывает из аккаунта.
      if (e instanceof HttpStatusError && isWrongCurrentPassword(e.status, e.message)) {
        setCurrentPasswordError(t('settingsWrongCurrentPassword'));
      } else {
        setPasswordError(humanizeError(e, t('passwordChangeFailed')));
      }
      return false;
    } finally {
      setPasswordBusy(false);
    }
  }

  /**
   * Удаление аккаунта (REL-1).
   *
   * Два барьера, и оба намеренные: пароль проверяет сервер (открытая чужая сессия — обычный
   * способ попасть в чужие настройки), слово подтверждения проверяется здесь и защищает от
   * промаха пальцем по кнопке. Слово берётся из локали: требовать русское «УДАЛИТЬ» от
   * человека с французским интерфейсом — значит требовать переключить раскладку ради того,
   * чтобы уйти.
   *
   * После успеха — выход. Сессии на сервере уже отозваны, и оставаться в приложении не с чем:
   * следующий же запрос вернул бы 401 и выкинул бы человека сам, но без единого слова.
   */
  async function submitAccountDeletion() {
    setDeletePasswordError(null);
    setDeleteError(null);
    if (!deletePassword.trim()) {
      setDeletePasswordError(t('settingsDeleteAccountPasswordRequired'));
      return;
    }
    const expected = t('settingsDeleteAccountConfirmWord').trim().toLocaleUpperCase();
    if (deleteWord.trim().toLocaleUpperCase() !== expected) {
      setDeleteError(t('settingsDeleteAccountWordMismatch'));
      return;
    }
    setDeleteBusy(true);
    try {
      await deleteOwnAccount(deletePassword);
      disarmAccountDeletion();
      onClose();
      await logout();
    } catch (e) {
      if (e instanceof HttpStatusError && isWrongCurrentPassword(e.status, e.message)) {
        setDeletePasswordError(t('settingsWrongCurrentPassword'));
      } else {
        setDeleteError(humanizeError(e, t('settingsDeleteAccountFailed')));
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  const handleLocalProblem = shareHandle.trim().length > 0 ? publicHandleProblem(handlePreview) : null;
  /**
   * Строка состояния под полем: сначала местные правила (они видны без сети), потом ответ
   * сервера о занятости. Пустое поле — не ошибка: это «снять ник», и подтверждение спросит
   * сама кнопка сохранения.
   */
  const handleStatusText: string | null = (() => {
    if (shareHandle.trim().length === 0) return null;
    if (handleLocalProblem === 'format') return t('settingsHandleFormat');
    if (handleLocalProblem === 'reserved') return t('settingsHandleReserved');
    if (handleLocalProblem === 'profanity') return t('settingsHandleNotAllowed');
    if (handlePreview === savedShareHandle) return null;
    if (handleCheckBusy) return t('settingsHandleChecking');
    if (!handleCheck || handleCheck.handle !== handlePreview) return null;
    if (handleCheck.available) return t('settingsHandleFree');
    if (handleCheck.problem === 'taken') return t('settingsHandleTaken');
    if (handleCheck.problem === 'reserved') return t('settingsHandleReserved');
    if (handleCheck.problem === 'profanity') return t('settingsHandleNotAllowed');
    return t('settingsHandleFormat');
  })();
  const handleStatusIsError =
    handleStatusText !== null &&
    handleStatusText !== t('settingsHandleFree') &&
    handleStatusText !== t('settingsHandleChecking');
  /** Кнопку держим закрытой, пока ник заведомо не пройдёт: поход в сеть за отказом бессмыслен. */
  const shareSaveBlocked =
    handleLocalProblem !== null ||
    handleCheckBusy ||
    (handlePreview.length > 0 &&
      handlePreview !== savedShareHandle &&
      handleCheck?.handle === handlePreview &&
      !handleCheck.available);
  const shareHandleDirty = shareHandle.trim().toLowerCase() !== savedShareHandle;
  const profilePageEnabled = user?.profilePublic !== false;
  const canCopyHandleTag = savedShareHandle.length >= 4 && !shareHandleDirty;
  const canShareProfileLink = canCopyHandleTag && profilePageEnabled;
  /**
   * Почему кнопка серая. Стори 18.6: раньше у самого частого случая — ник ещё не задан — причины
   * не было вовсе, и две мёртвые кнопки выглядели просто сломанными.
   */
  const shareButtonsBlockedReason: MessageKey | null =
    savedShareHandle.length >= 4 && shareHandleDirty
      ? 'settingsShareDisabledUnsaved'
      : savedShareHandle.length >= 4 && !profilePageEnabled
        ? 'settingsShareDisabledHidden'
        : savedShareHandle.length < 4
          ? shareHandle.trim().length >= 4
            ? 'settingsShareDisabledUnsaved'
            : 'settingsShareDisabledNoHandle'
          : null;
  /** Ника нет совсем (и не набирается) — делимся общей ссылкой-приглашением (п. 40). */
  const shareInviteInstead = shareButtonsBlockedReason === 'settingsShareDisabledNoHandle';

  /**
   * Разделы формы привязываются к таблице стилей один раз на тему, а не заново на каждый рендер:
   * см. комментарий у `bindSettingsControls`. Call-site'ы от этого не меняются — `Group`,
   * `SaveButton` и `ToggleRow` здесь те же, что и раньше.
   */
  const { Group, SaveButton, ToggleRow } = useMemo(
    () => bindSettingsControls(styles, listPal.settingsFieldLabel),
    [listPal.settingsFieldLabel, styles],
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => void close()}>
      <View
        style={[
          styles.modalBg,
          Platform.OS === 'web' ? { alignItems: 'center' } : null,
          keyboard > 0 ? { paddingBottom: keyboard } : null,
        ]}>
        <View
          style={[
            styles.modalCard,
            styles.settingsCard,
            Platform.OS === 'web' ? { maxWidth: 480, width: '100%', alignSelf: 'center' } : null,
          ]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={[styles.modalTitle, { flex: 1 }]} accessibilityRole="header">
              {t('gearSettings')}
            </Text>
            {/* Выход из окна — в шапке, а не только внизу длинного свитка. */}
            <Pressable
              onPress={() => void close()}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={t('close')}
              style={styles.iconSquareBtn}>
              <Text style={styles.iconSquareBtnGlyph}>×</Text>
            </Pressable>
          </View>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }} accessibilityRole="tablist">
            {TABS.map((item) => (
              <SettingsTabChip
                key={item.id}
                styles={styles}
                label={t(item.label)}
                active={tab === item.id}
                onPress={() => selectTab(item.id)}
              />
            ))}
          </View>

          {notice ? (
            <Text style={styles.settingsSavedNote} accessibilityLiveRegion="polite">
              {notice}
            </Text>
          ) : null}

          <ScrollView
            ref={settingsScrollRef}
            // С клавиатурой — не выше места над ней за вычетом шапки окна и вкладок.
            style={{
              maxHeight:
                keyboard > 0
                  ? Math.max(160, window.height - keyboard - 300)
                  : Math.min(580, window.height * 0.72),
            }}
            contentContainerStyle={{ gap: 12, paddingTop: 4, paddingBottom: 8 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator>
            {/* Обёртка — точка отсчёта, чтобы подкрутить к полю с курсором (см. keyboard выше). */}
            <View ref={settingsContentRef} collapsable={false} style={{ gap: 12 }}>
            {tab === 'profile' ? (
              <>
                <Group title={t('settingsProfileSection')}>
                  <Text style={styles.settingsFieldLabel}>{t('settingsChangeNameLabel')}</Text>
                  <TextInput
                    placeholder={t('settingsNamePlaceholder')}
                    value={profileDisplayName}
                    onChangeText={setProfileDisplayName}
                    maxLength={DISPLAY_NAME_MAX_CHARS}
                    style={styles.input}
                    accessibilityLabel={t('settingsChangeNameLabel')}
                  />
                  {/* Пустое имя — ошибка этого поля, и стоит она под ним, а не под кнопкой (п. 8). */}
                  {profileError === t('settingsNameRequired') ? (
                    <Text style={styles.dmErr} accessibilityLiveRegion="assertive" accessibilityRole="alert">
                      {profileError}
                    </Text>
                  ) : null}
                  <Text style={styles.settingsFieldLabel}>{t('settingsStatusLabel')}</Text>
                  <TextInput
                    placeholder={t('settingsStatusPlaceholder')}
                    value={profileStatusText}
                    onChangeText={setProfileStatusText}
                    style={styles.input}
                    maxLength={STATUS_TEXT_MAX_CHARS}
                    accessibilityLabel={t('settingsStatusLabel')}
                  />
                  <Text style={styles.settingsFieldLabel}>{t('settingsBioLabel')}</Text>
                  <TextInput
                    placeholder={t('settingsBioPlaceholder')}
                    value={profileBio}
                    onChangeText={setProfileBio}
                    style={[styles.input, { minHeight: 72 }]}
                    multiline
                    maxLength={BIO_MAX_CHARS}
                    accessibilityLabel={t('settingsBioLabel')}
                  />
                  {/*
                    Пол и дата рождения (заказ владельца 2026-08-29). Оба стоят под «о себе»,
                    в той же форме и под той же кнопкой: это части профиля, а не отдельная
                    настройка, и своя кнопка «сохранить» у каждой была бы ровно тем, на что
                    жаловался владелец в 18.6 («четыре формы подряд, четыре „сохранить“»).
                  */}
                  <Text style={styles.settingsFieldLabel}>{t('settingsGenderLabel')}</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                    {GENDER_CHOICES.map((choice) => {
                      const active = profileGender === choice.value;
                      return (
                        <Pressable
                          key={choice.label}
                          onPress={() => {
                            hapticSelection();
                            setProfileGender(choice.value);
                          }}
                          accessibilityRole="button"
                          accessibilityState={{ selected: active }}
                          accessibilityLabel={t(choice.label)}
                          style={[
                            styles.chatFilterChip,
                            TAB_CHIP_LAYOUT,
                            active ? styles.chatFilterChipActive : null,
                          ]}>
                          <Text
                            style={active ? styles.chatFilterChipTextActive : styles.chatFilterChipText}>
                            {t(choice.label)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                  <Text style={styles.settingsFieldLabel}>{t('settingsBirthDateLabel')}</Text>
                  {/* Тем же полем, что дата напоминания: в вебе это <input type="date">, и
                      браузер сам показывает календарь. Обычный TextInput на телефоне через
                      браузер календаря не открывал вовсе. */}
                  <DateTimeField
                    kind="date"
                    value={profileBirthDate}
                    onChange={setProfileBirthDate}
                    placeholder={t('reminderDatePlaceholder')}
                    accessibilityLabel={t('settingsBirthDateLabel')}
                    style={styles.input}
                    /* Возрастная вилка отдаётся календарю: недопустимый год не должен
                       выбираться, а не отвергаться уже после «Сохранить». */
                    minDate={birthDateCalendarBounds.min}
                    maxDate={birthDateCalendarBounds.max}
                  />
                  {/* Подсказка объясняет и формат, и последствие: заполнив дату, человек
                      соглашается, что контактам придёт напоминание. Прятать это в согласии
                      при регистрации было бы нечестно — там о такой функции ещё не знали. */}
                  <Text style={styles.settingsInlineHint}>
                    {t('settingsBirthDateHint')
                      .replace('{min}', String(BIRTH_AGE_MIN_YEARS))
                      .replace('{max}', String(BIRTH_AGE_MAX_YEARS))}
                  </Text>
                  <SaveButton
                    label={t('settingsSaveProfile')}
                    busy={profileBusy}
                    onPress={() => void submitProfileUpdate()}
                  />
                  {profileError && profileError !== t('settingsNameRequired') ? (
                    <Text style={styles.dmErr} accessibilityLiveRegion="assertive" accessibilityRole="alert">
                      {profileError}
                    </Text>
                  ) : null}
                  {!profileError && profileSaved ? (
                    <Text style={styles.settingsSavedNote}>{t('settingsProfileSaved')}</Text>
                  ) : null}
                </Group>

                {/*
                  Стори 18.6: фото — соседняя вкладка, а не спрятанный раздел. Строка-переход
                  здесь, потому что «изменить фото» человек ищет именно в профиле.
                */}
                <Pressable
                  onPress={() => selectTab('avatar')}
                  accessibilityRole="button"
                  accessibilityLabel={t('settingsAvatarTitle')}
                  style={[
                    styles.settingsGroupCard,
                    { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
                  ]}>
                  <Text style={styles.settingsGroupTitle}>{t('settingsAvatarTitle')}</Text>
                  <Text style={styles.iconSquareBtnGlyph}>›</Text>
                </Pressable>

                <SettingsDoneRow
                  styles={styles}
                  label={t('settingsPreferencesDone')}
                  busy={doneBusy}
                  onPress={() => void saveDirtyAndClose()}
                />
              </>
            ) : tab === 'avatar' ? (
              <AvatarSettingsSection onClose={() => void close()} />
            ) : tab === 'account' ? (
              <>
                <Group title={t('settingsLoginSection')}>
                  {/*
                    Логин и ник — два разных имени, и путали их постоянно (аудит 2026-10-04, §4):
                    публичное имя одно — #ник, а логин нужен только для входа. Говорим это прямо
                    над полем, а не прячем логин: им до сих пор входят те, кто завёл его сам.
                  */}
                  <Text style={styles.settingsInlineHint}>{t('settingsLoginExplain')}</Text>
                  <Text style={styles.settingsFieldLabel}>{t('settingsChangeLoginLabel')}</Text>
                  <TextInput
                    placeholder={t('loginField')}
                    value={profileLogin}
                    onChangeText={changeProfileLogin}
                    maxLength={LOGIN_MAX_CHARS}
                    style={styles.input}
                    autoCapitalize="none"
                    autoCorrect={false}
                    accessibilityLabel={t('settingsChangeLoginLabel')}
                  />
                  <Text style={styles.settingsInlineHint}>
                    {profileLoginCheckBusy
                      ? t('loginCheckInProgress')
                      : profileLoginAvailable === false
                        ? t('loginUnavailable')
                        : profileLoginAvailable === true
                          ? t('loginAvailable')
                          : t('settingsLoginHint')}
                  </Text>
                  <SaveButton
                    label={t('settingsSaveLogin')}
                    busy={loginBusy}
                    disabled={profileLoginCheckBusy || profileLoginAvailable === false}
                    onPress={() => void submitLoginUpdate()}
                  />
                  {loginError ? <Text style={styles.dmErr}>{loginError}</Text> : null}
                  {!loginError && loginSaved ? (
                    <Text style={styles.settingsSavedNote}>{t('settingsLoginSaved')}</Text>
                  ) : null}
                </Group>

                <Group title={t('settingsPasswordSection')}>
                  <Text style={styles.settingsFieldLabel}>{t('currentPassword')}</Text>
                  <TextInput
                    placeholder={t('currentPassword')}
                    value={currentPassword}
                    onChangeText={(v) => {
                      setCurrentPassword(v);
                      setCurrentPasswordError(null);
                    }}
                    maxLength={PASSWORD_MAX_CHARS}
                    style={styles.input}
                    secureTextEntry
                    autoCapitalize="none"
                    textContentType="password"
                    autoComplete="password"
                    accessibilityLabel={t('currentPassword')}
                  />
                  {currentPasswordError ? (
                    <Text
                      style={styles.dmErr}
                      accessibilityLiveRegion="assertive"
                      accessibilityRole="alert">
                      {currentPasswordError}
                    </Text>
                  ) : null}
                  <Text style={styles.settingsFieldLabel}>{t('newPasswordLabel')}</Text>
                  <TextInput
                    placeholder={t('newPasswordLabel')}
                    value={newPassword}
                    onChangeText={setNewPassword}
                    maxLength={PASSWORD_MAX_CHARS}
                    style={styles.input}
                    secureTextEntry
                    autoCapitalize="none"
                    textContentType="newPassword"
                    autoComplete="new-password"
                    accessibilityLabel={t('newPasswordLabel')}
                  />
                  {passwordError ? (
                    <Text style={styles.dmErr} accessibilityLiveRegion="assertive" accessibilityRole="alert">
                      {passwordError}
                    </Text>
                  ) : null}
                  <SaveButton
                    label={t('savePassword')}
                    busy={passwordBusy}
                    onPress={() => void submitPasswordChange()}
                  />
                  {!passwordError && passwordSaved ? (
                    <Text style={styles.settingsSavedNote}>{t('settingsPasswordChanged')}</Text>
                  ) : null}
                </Group>

                <Group title={t('settingsLegalSection')}>
                  <Pressable
                    onPress={() => void Linking.openURL(`${PUBLIC_WEB_ORIGIN}/legal/privacy`)}
                    accessibilityRole="link"
                    accessibilityLabel={t('settingsPrivacyPolicyLink')}>
                    <Text style={[styles.settingsInlineHint, LEGAL_LINK]}>
                      {t('settingsPrivacyPolicyLink')}
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => void Linking.openURL(`${PUBLIC_WEB_ORIGIN}/legal/terms`)}
                    accessibilityRole="link"
                    accessibilityLabel={t('settingsTermsLink')}>
                    <Text style={[styles.settingsInlineHint, LEGAL_LINK]}>{t('settingsTermsLink')}</Text>
                  </Pressable>
                </Group>

                {/*
                  REL-1. Удаление аккаунта — требование Apple 5.1.1(v) и Google Play. Раздел стоит
                  последним и отделён рамкой: он не должен попадаться под палец по дороге к смене
                  пароля.
                */}
                <Group title={t('settingsDeleteAccountSection')}>
                  <Text style={styles.settingsInlineHint}>{t('settingsDeleteAccountWarning')}</Text>
                  {!deleteArmed ? (
                    <Pressable
                      style={[styles.modalBtn, DANGER_BTN_OUTLINE, { alignSelf: 'flex-start' }]}
                      onPress={() => {
                        setDeleteError(null);
                        setDeleteArmed(true);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={t('settingsDeleteAccountStart')}>
                      <Text style={[styles.modalBtnText, DANGER_TEXT]}>
                        {t('settingsDeleteAccountStart')}
                      </Text>
                    </Pressable>
                  ) : (
                    <>
                      <Text style={styles.settingsFieldLabel}>
                        {t('settingsDeleteAccountConfirmHint')}
                      </Text>
                      <TextInput
                        placeholder={t('currentPassword')}
                        value={deletePassword}
                        onChangeText={(v) => {
                          setDeletePassword(v);
                          setDeletePasswordError(null);
                        }}
                        maxLength={PASSWORD_MAX_CHARS}
                        style={styles.input}
                        secureTextEntry
                        autoCapitalize="none"
                        accessibilityLabel={t('currentPassword')}
                      />
                      {deletePasswordError ? (
                        <Text
                          style={styles.dmErr}
                          accessibilityLiveRegion="assertive"
                          accessibilityRole="alert">
                          {deletePasswordError}
                        </Text>
                      ) : null}
                      <TextInput
                        placeholder={t('settingsDeleteAccountConfirmPlaceholder')}
                        value={deleteWord}
                        onChangeText={setDeleteWord}
                        maxLength={32}
                        style={styles.input}
                        autoCapitalize="characters"
                        autoCorrect={false}
                        accessibilityLabel={t('settingsDeleteAccountConfirmPlaceholder')}
                      />
                      {deleteError ? <Text style={styles.dmErr}>{deleteError}</Text> : null}
                      <Pressable
                        style={[
                          styles.modalBtn,
                          DANGER_BTN_SOLID,
                          { alignSelf: 'flex-start' },
                          deleteBusy ? styles.modalBtnDisabled : null,
                        ]}
                        onPress={() => void submitAccountDeletion()}
                        disabled={deleteBusy}
                        accessibilityRole="button"
                        accessibilityLabel={t('settingsDeleteAccountConfirm')}
                        accessibilityState={{ disabled: deleteBusy, busy: deleteBusy }}>
                        {deleteBusy ? <ActivityIndicator size="small" color="#fff" /> : null}
                        <Text style={[styles.modalBtnText, { color: '#fff' }]}>
                          {t('settingsDeleteAccountConfirm')}
                        </Text>
                      </Pressable>
                      <Pressable
                        onPress={disarmAccountDeletion}
                        disabled={deleteBusy}
                        style={[MIN_TAP, { alignSelf: 'flex-start', paddingHorizontal: 4 }]}
                        accessibilityRole="button"
                        accessibilityLabel={t('settingsDeleteAccountCancel')}>
                        <Text style={styles.settingsInlineHint}>
                          {t('settingsDeleteAccountCancel')}
                        </Text>
                      </Pressable>
                    </>
                  )}
                </Group>

                {/*
                  Выход — здесь, внизу «Аккаунта», и с вопросом (п. 12). Раньше это была иконка
                  в шапке списка чатов рядом с шестерёнкой: промах по соседней кнопке выкидывал
                  из аккаунта без единого слова.
                */}
                <Pressable
                  style={[styles.modalBtn, DANGER_BTN_OUTLINE, { alignSelf: 'stretch', minHeight: 44 }]}
                  onPress={() => void confirmLogout()}
                  accessibilityRole="button"
                  accessibilityLabel={t('logout')}>
                  <Text style={[styles.modalBtnText, DANGER_TEXT]}>{t('logout')}</Text>
                </Pressable>

                <SettingsDoneRow
                  styles={styles}
                  label={t('settingsPreferencesDone')}
                  busy={doneBusy}
                  onPress={() => void saveDirtyAndClose()}
                />
              </>
            ) : tab === 'privacy' ? (
              <>
                <Group title={t('settingsShareSection')}>
                  <Text style={styles.settingsFieldLabel}>{t('settingsPublicHandleLabel')}</Text>
                  <TextInput
                    placeholder={t('settingsPublicHandlePlaceholder')}
                    value={shareHandle}
                    // Пробелы срезаем на вводе — набранный по ошибке пробел иначе тихо исчезает
                    // при сохранении, и человек видит не тот ник, который вводил. Остальное не
                    // переписываем: подсказка ниже показывает будущую форму, а спорить с
                    // печатающим человеком посимвольно — худший из способов её объяснить.
                    onChangeText={(v) => changeShareHandle(v.replace(/\s+/g, ''))}
                    style={styles.input}
                    autoCapitalize="none"
                    autoCorrect={false}
                    maxLength={64}
                    accessibilityLabel={t('settingsPublicHandleLabel')}
                  />
                  {/*
                    Стори 18.2. Три строки под полем в порядке важности: во что превратится ввод,
                    свободен ли ник, и общие правила. Раньше была только третья, а расхождение
                    ввода с сохранённым и занятость ника человек узнавал уже после «Сохранить» —
                    английской строкой от сервера.
                  */}
                  {handlePreview && handlePreview !== shareHandle.trim() ? (
                    <Text style={styles.settingsInlineHint}>
                      {t('settingsHandleWillSaveAs').replace('{handle}', handlePreview)}
                    </Text>
                  ) : null}
                  {handleStatusText ? (
                    <Text style={handleStatusIsError ? styles.dmErr : styles.settingsSavedNote}>
                      {handleStatusText}
                    </Text>
                  ) : null}
                  {handleSuggestions.length > 0 ? (
                    <>
                      <Text style={styles.settingsInlineHint}>{t('settingsHandleSuggestions')}</Text>
                      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 2 }}>
                        {handleSuggestions.map((suggestion) => (
                          <Pressable
                            key={suggestion}
                            style={styles.modalBtn}
                            onPress={() => changeShareHandle(suggestion)}
                            accessibilityRole="button"
                            accessibilityLabel={`#${suggestion}`}>
                            <Text style={styles.modalBtnText}>#{suggestion}</Text>
                          </Pressable>
                        ))}
                      </View>
                    </>
                  ) : null}
                  <Text style={styles.settingsInlineHint}>{t('settingsPublicHandleHint')}</Text>
                  <SaveButton
                    label={t('settingsSaveShareSettings')}
                    busy={shareBusy}
                    disabled={shareSaveBlocked}
                    onPress={() => void submitHandle()}
                  />
                  {shareError ? <Text style={styles.dmErr}>{shareError}</Text> : null}
                  {!shareError && shareSaved ? (
                    <Text style={styles.settingsSavedNote}>{t('settingsShareSaved')}</Text>
                  ) : null}
                  {/*
                    Стори 18.2: кнопок две, а не три. «Скопировать ссылку» строила тот же URL, что и
                    «Поделиться», а в вебе `Share.share` отваливается и первая кнопка в catch делала
                    ровно то же — два одинаковых действия с одинаковым тостом.

                    Стори 18.3. Кнопки работают по СОХРАНЁННОМУ нику (`user.publicHandle`), а не по
                    тексту поля: раньше хватало набрать четыре любых символа и, ничего не сохранив,
                    разослать ссылку на чужой или несуществующий профиль. И ссылка глохнет вместе с
                    «Публичной страницей профиля»: при выключенном флаге сервер отдаёт по ней 404.
                  */}
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {/*
                      Аудит 2026-10-04, п. 40: кнопка больше не бывает мёртвой. Без ника она
                      отправляет общую ссылку-приглашение — новичку без ника иначе было нечем
                      позвать первого собеседника. Когда ник есть, но ссылка ещё не готова
                      (не сохранён, профиль скрыт), нажатие говорит, что сделать.
                    */}
                    <Pressable
                      style={[styles.modalBtn, styles.modalPrimary, { alignSelf: 'flex-start' }]}
                      accessibilityRole="button"
                      accessibilityLabel={shareInviteInstead ? t('settingsInviteShare') : t('shareProfile')}
                      onPress={() => {
                        if (!canShareProfileLink && !shareInviteInstead) {
                          if (shareButtonsBlockedReason) showNotice(t(shareButtonsBlockedReason));
                          return;
                        }
                        const url = canShareProfileLink
                          ? buildPublicProfileUrl(savedShareHandle)
                          : PUBLIC_WEB_ORIGIN;
                        const message = canShareProfileLink
                          ? url
                          : t('settingsInviteMessage').replace('{url}', url);
                        // Системный лист есть не везде (в вебе — почти нигде): запасной путь
                        // кладёт ссылку в буфер и говорит об этом строкой, а не мёртвым Alert.
                        Share.share({ message, url }).catch(() => {
                          Clipboard.setString(message);
                          showNotice(t('settingsCopied'));
                        });
                      }}>
                      <Text style={[styles.modalBtnText, { color: '#fff' }]}>
                        {shareInviteInstead ? t('settingsInviteShare') : t('shareProfile')}
                      </Text>
                    </Pressable>
                    <Pressable
                      style={[
                        styles.modalBtn,
                        canCopyHandleTag ? null : styles.modalBtnDisabled,
                      ]}
                      disabled={!canCopyHandleTag}
                      accessibilityRole="button"
                      accessibilityLabel={t('settingsCopyHandleTag')}
                      accessibilityState={{ disabled: !canCopyHandleTag }}
                      onPress={() => {
                        if (!canCopyHandleTag) return;
                        Clipboard.setString(`#${savedShareHandle}`);
                        showNotice(t('settingsCopied'));
                      }}>
                      <Text style={[styles.modalBtnText, { color: listPal.modalBtnText }]}>
                        {t('settingsCopyHandleTag')}
                      </Text>
                    </Pressable>
                  </View>
                  {/* Молча выключенная кнопка неотличима от сломанной — причина всегда рядом. */}
                  {shareInviteInstead ? (
                    <Text style={styles.settingsInlineHint}>{t('settingsShareNoHandleInvite')}</Text>
                  ) : shareButtonsBlockedReason ? (
                    <Text style={styles.settingsInlineHint}>{t(shareButtonsBlockedReason)}</Text>
                  ) : null}
                </Group>

                <Group title={t('settingsVisibilitySection')}>
                  <ToggleRow
                    label={t('settingsProfilePublicLabel')}
                    value={shareProfilePublic}
                    disabled={visibilityBusy}
                    onValueChange={(v) => void toggleVisibility({ profilePublic: v })}
                  />
                  <Text style={styles.settingsInlineHint}>{t('settingsProfilePublicHint')}</Text>
                  <View style={styles.settingsSectionDivider} />
                  <ToggleRow
                    label={t('settingsAllowDiscoveryLabel')}
                    value={shareAllowDiscovery}
                    disabled={visibilityBusy}
                    onValueChange={(v) => void toggleVisibility({ allowDiscoveryByHandle: v })}
                  />
                  <Text style={styles.settingsInlineHint}>{t('settingsAllowDiscoveryHint')}</Text>
                  {visibilityError ? <Text style={styles.dmErr}>{visibilityError}</Text> : null}
                  {!visibilityError && visibilitySaved ? (
                    <Text style={styles.settingsSavedNote}>{t('settingsSaved')}</Text>
                  ) : null}
                </Group>

                <Group title={t('settingsMediaPrivacySection')}>
                  {/*
                    Стори 13.11 (переставлено 18.1, переехало 18.6): настройка местная и
                    сохраняется сама. Во вкладку «Фото» её класть нельзя: та про аватар, к
                    отправке снимков отношения не имеет.
                  */}
                  <ToggleRow
                    label={t('settingsKeepPhotoMetadataLabel')}
                    value={keepPhotoMetadata}
                    onValueChange={(v) => {
                      setKeepPhotoMetadata(v);
                      void writeKeepPhotoMetadata(v);
                    }}
                  />
                  <Text style={styles.settingsInlineHint}>{t('settingsLocalOnlyHint')}</Text>
                  <Text style={styles.settingsInlineHint}>{t('settingsKeepPhotoMetadataHint')}</Text>
                </Group>

                {/*
                  REL-2. Заблокировать можно из чата, а снять блокировку — только отсюда:
                  чат с заблокированным человек, как правило, уже не открывает, и без этого
                  списка блокировка была бы необратимой на практике.
                */}
                <Group title={t('settingsBlockedSection')}>
                  {blockedBusy ? (
                    <ActivityIndicator size="small" />
                  ) : blocked.length === 0 ? (
                    <Text style={styles.settingsInlineHint}>{t('settingsBlockedEmpty')}</Text>
                  ) : (
                    blocked.map((b) => (
                      <View key={b.userId} style={BLOCKED_ROW}>
                        <Text style={styles.settingsFieldLabel}>{b.displayName}</Text>
                        <Pressable
                          onPress={() => void submitUnblock(b.userId)}
                          style={[MIN_TAP, { paddingHorizontal: 8 }]}
                          accessibilityRole="button"
                          accessibilityLabel={`${t('unblockAction')}: ${b.displayName}`}>
                          <Text style={[styles.settingsInlineHint, LEGAL_LINK]}>{t('unblockAction')}</Text>
                        </Pressable>
                      </View>
                    ))
                  )}
                  {blockedError ? <Text style={styles.dmErr}>{blockedError}</Text> : null}
                </Group>

                <SettingsDoneRow
                  styles={styles}
                  label={t('settingsPreferencesDone')}
                  busy={doneBusy}
                  onPress={() => void saveDirtyAndClose()}
                />
              </>
            ) : (
              <>
                <Group title={t('settingsAppearanceSection')}>
                  <Text style={styles.settingsFieldLabel}>{t('settingsVisualThemeLabel')}</Text>
                  <ThemePickerRow
                    visualTheme={visualTheme}
                    onSelect={setVisualTheme}
                    palette={{
                      chipBg: listPal.modalBtn,
                      chipBorder: listPal.smallBtnBorder,
                      chipText: listPal.modalBtnText,
                      chipSelectedBg: listPal.chatFilterChipActive,
                      chipSelectedBorder: listPal.chatFilterChipActiveBorder,
                      chipSelectedText: listPal.chatFilterChipTextActive,
                    }}
                  />
                  {/*
                    Стори 18.1: тактильная отдача переехала сюда из «Публичной ссылки и ника».
                    Там она стояла между двумя серверными тумблерами, над кнопкой «Сохранить»,
                    которая её не сохраняла, — и выглядела ровно так же, как они.
                  */}
                  <ToggleRow
                    label={t('settingsHapticsLabel')}
                    value={hapticsEnabled}
                    onValueChange={(v) => {
                      setHapticsEnabledState(v);
                      // В память — сразу, чтобы следующий же отклик учёл выбор; в хранилище —
                      // следом. Ждать записи здесь значило бы держать переключатель мёртвым
                      // ровно в тот момент, когда человек проверяет, что он делает.
                      setHapticsEnabled(v);
                      if (v) hapticSelection();
                      void writeHapticsEnabled(v);
                    }}
                  />
                  <Text style={styles.settingsInlineHint}>{t('settingsLocalOnlyHint')}</Text>
                </Group>

                {/*
                  Уведомления — свой раздел (аудит 2026-10-04, п. 13). Раньше звук жил среди
                  оформления, «Не беспокоить» — только в быстрых настройках по тапу на своё имя,
                  а дороги в системные настройки уведомлений не было вовсе.
                */}
                <Group title={t('settingsNotificationsSection')}>
                  {/*
                    «Режим без звука» на стороне приложения. Системный беззвучный режим
                    телефона уважается отдельно и без всякой настройки — через категорию
                    аудиосессии в проигрывателях (см. `audioCategoryFor`). Этот тумблер для
                    другого случая: телефон со звуком, а конкретно от мессенджера звука не
                    хочется. Тем же приёмом, что и отдача: сразу в память, потом на диск.
                  */}
                  <ToggleRow
                    label={t('settingsSoundsLabel')}
                    value={soundsEnabled}
                    onValueChange={(v) => {
                      setSoundsEnabledState(v);
                      setSoundsEnabled(v);
                      hapticSelection();
                      // Каналы уведомлений Android держат звук в себе и не перенастраиваются
                      // после создания: сбрасываем память о заведённых, чтобы следующее
                      // уведомление создало канал нужной звучности.
                      invalidateAndroidChannels();
                      void writeSoundsEnabled(v);
                    }}
                  />
                  <Text style={styles.settingsInlineHint}>{t('settingsLocalOnlyHint')}</Text>
                  <View style={styles.settingsSectionDivider} />
                  <ToggleRow
                    label={t('dndLabel')}
                    value={dndDraft ?? user?.doNotDisturb === true}
                    disabled={dndDraft !== null}
                    onValueChange={(v) => void toggleDnd(v)}
                  />
                  <Text style={styles.settingsInlineHint}>{t('settingsNotificationsNote')}</Text>
                  {dndError ? <Text style={styles.dmErr}>{dndError}</Text> : null}
                  <View style={styles.settingsSectionDivider} />
                  <ToggleRow
                    label={t('notifyShowTextLabel')}
                    value={showTextDraft ?? user?.showMessageText !== false}
                    disabled={showTextDraft !== null}
                    onValueChange={(v) => void toggleShowText(v)}
                  />
                  <Text style={styles.settingsInlineHint}>{t('notifyShowTextHint')}</Text>
                  {showTextError ? <Text style={styles.dmErr}>{showTextError}</Text> : null}
                  <View style={styles.settingsSectionDivider} />
                  {Platform.OS === 'web' ? (
                    // В браузере «системных настроек» нет: разрешение даёт сам сайт, и спросить
                    // его можно только по нажатию — отсюда кнопка, а не запрос на входе (п. 15).
                    notifyStatus === 'granted' ? (
                      <Text style={styles.settingsSavedNote}>{t('settingsNotificationsOn')}</Text>
                    ) : notifyStatus === 'denied' ? (
                      <Text style={styles.settingsInlineHint}>{t('settingsNotificationsBlocked')}</Text>
                    ) : notifyStatus === 'undetermined' ? (
                      <Pressable
                        style={[styles.modalBtn, styles.modalPrimary, { alignSelf: 'flex-start' }]}
                        onPress={() => void enableNotifications()}
                        accessibilityRole="button"
                        accessibilityLabel={t('settingsEnableNotifications')}>
                        <Text style={[styles.modalBtnText, { color: '#fff' }]}>
                          {t('settingsEnableNotifications')}
                        </Text>
                      </Pressable>
                    ) : null
                  ) : (
                    <>
                      {notifyStatus === 'undetermined' ? (
                        <Pressable
                          style={[styles.modalBtn, styles.modalPrimary, { alignSelf: 'flex-start' }]}
                          onPress={() => void enableNotifications()}
                          accessibilityRole="button"
                          accessibilityLabel={t('settingsEnableNotifications')}>
                          <Text style={[styles.modalBtnText, { color: '#fff' }]}>
                            {t('settingsEnableNotifications')}
                          </Text>
                        </Pressable>
                      ) : null}
                      <Pressable
                        style={[styles.modalBtn, { alignSelf: 'flex-start' }]}
                        onPress={() => void Linking.openSettings()}
                        accessibilityRole="button"
                        accessibilityLabel={t('settingsOpenSystemSettings')}>
                        <Text style={[styles.modalBtnText, { color: listPal.modalBtnText }]}>
                          {t('settingsOpenSystemSettings')}
                        </Text>
                      </Pressable>
                    </>
                  )}
                </Group>

                <Group title={t('settingsLanguageSection')}>
                  <Text style={styles.settingsFieldLabel}>{t('settingsLanguageLabel')}</Text>
                  <LanguagePickerRow
                    locale={locale}
                    onSelect={setLocale}
                    palette={{
                      chipBg: listPal.modalBtn,
                      chipBorder: listPal.smallBtnBorder,
                      chipSelectedBg: listPal.chatFilterChipActive,
                      chipSelectedBorder: listPal.chatFilterChipActiveBorder,
                    }}
                  />
                  <Text style={styles.hint}>{t('settingsPreferencesHint')}</Text>
                </Group>

                {/*
                  Блок «Система» (переход в «Аккаунты звонков» Android) убран в REL-3.

                  Стори 18.1 поставила его сюда как запасной путь на случай, когда Telecom не
                  показывает входящий. Сам Telecom на Android больше не участвует ни в одном
                  пути входящего звонка, а его разрешения (`CALL_PHONE`, `READ_PHONE_STATE`,
                  `READ_CALL_LOG`) стоят публикации в Google Play и убраны из манифеста. Без
                  них приложение не регистрирует PhoneAccount — то есть кнопка вела бы в
                  системный список, в котором SVYAZ отсутствует. Пустой экран по кнопке хуже,
                  чем отсутствие кнопки.
                */}
                <SettingsDoneRow
                  styles={styles}
                  label={t('settingsPreferencesDone')}
                  busy={doneBusy}
                  onPress={() => void saveDirtyAndClose()}
                />
              </>
            )}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}
